import React, { useState } from 'react';
import { coursesApi, mediaApi } from '../../api/client';
import { DropZone, actionsStyle, errorBanner, formStyle, hintStyle, inputStyle, labelStyle, pageStyle, primaryButton } from '../formUi';

type QType = 'multiple_choice' | 'true_false' | 'fill_blank';
type Question = {
  question: string;
  questionType: QType;
  options: string[];
  correctAnswer: number;
  correctAnswerText: string;
  caseSensitive: boolean;
  fuzzyMatching: boolean;
};

const blankQuestion = (): Question => ({
  question: '',
  questionType: 'multiple_choice',
  options: ['', '', '', ''],
  correctAnswer: 0,
  correctAnswerText: '',
  caseSensitive: false,
  fuzzyMatching: false,
});

const CourseCreateForm: React.FC<{ onCancel: () => void; onCreated: () => void }> = ({ onCancel, onCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [contentType, setContentType] = useState<'video' | 'pdf' | 'both'>('video');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfTitle, setPdfTitle] = useState('');
  const [passingScore, setPassingScore] = useState(70);
  const [duration, setDuration] = useState('');
  const [category, setCategory] = useState('');
  const [randomizeQuestions, setRandomizeQuestions] = useState(false);
  const [randomizeOptions, setRandomizeOptions] = useState(false);
  const [useQuestionPool, setUseQuestionPool] = useState(false);
  const [poolSize, setPoolSize] = useState(10);
  const [questionsToDisplay, setQuestionsToDisplay] = useState(5);
  const [quiz, setQuiz] = useState<Question[]>([blankQuestion()]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setType = (index: number, questionType: QType) => {
    setQuiz((prev) =>
      prev.map((q, i) => {
        if (i !== index) return q;
        if (questionType === 'true_false') return { ...q, questionType, options: ['True', 'False'], correctAnswer: 0 };
        if (questionType === 'fill_blank') return { ...q, questionType, options: [] };
        return { ...q, questionType, options: ['', '', '', ''], correctAnswer: 0 };
      })
    );
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      let videoKey: string | null = null;
      let imageKey: string | null = null;
      let pdfKey: string | null = null;
      if (videoFile) videoKey = (await mediaApi.upload(videoFile)).key;
      if (imageFile) imageKey = (await mediaApi.upload(imageFile)).key;
      if (pdfFile) pdfKey = (await mediaApi.upload(pdfFile)).key;
      const course = await coursesApi.create({
        title,
        description,
        contentType,
        passingScore,
        duration: duration || null,
        category: category || null,
        status: 'published',
        videoKey,
        imageKey,
        pdfKey,
        pdfTitle: pdfTitle || null,
        randomizeQuestions,
        randomizeOptions,
        useQuestionPool,
        poolSize: useQuestionPool ? poolSize : null,
        questionsToDisplay: useQuestionPool ? questionsToDisplay : null,
      });
      for (const q of quiz) {
        if (!q.question.trim()) continue;
        await coursesApi.addQuestion(course.id, {
          question: q.question,
          questionType: q.questionType,
          options: q.questionType === 'fill_blank' ? [] : q.options,
          correctAnswer: q.questionType === 'fill_blank' ? null : q.correctAnswer,
          correctAnswerText: q.questionType === 'fill_blank' ? q.correctAnswerText : null,
          caseSensitive: q.caseSensitive,
          fuzzyMatching: q.fuzzyMatching,
        });
      }
      onCreated();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={pageStyle(800)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Create New Course</h2>
        <button type="button" onClick={onCancel} style={{ padding: '0.5rem 1rem', backgroundColor: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
      <form onSubmit={submit} style={formStyle}>
        {error && <div style={errorBanner}>{error}</div>}
        <div>
          <label style={labelStyle}>Course Title *</label>
          <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter course title" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Course Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Enter course description" rows={4} style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
        <div>
          <label style={labelStyle}>Content Type *</label>
          <select value={contentType} onChange={(e) => setContentType(e.target.value as any)} style={{ ...inputStyle, background: 'white' }}>
            <option value="video">🎥 Video Only</option>
            <option value="pdf">📄 PDF Document Only</option>
            <option value="both">🎥📄 Both Video and PDF</option>
          </select>
          <p style={hintStyle}>Select the type of content this course will contain. You can upload both video and PDF files.</p>
        </div>
        {(contentType === 'video' || contentType === 'both') && (
          <DropZone label={contentType === 'both' ? 'Course Video (optional if PDF is provided)' : 'Course Video *'} accept="video/*" file={videoFile} onFile={setVideoFile} />
        )}
        <DropZone label="Course Image/Thumbnail (optional)" hint="Supported formats: JPG, PNG, GIF" accept="image/*" file={imageFile} onFile={setImageFile} />
        {(contentType === 'pdf' || contentType === 'both') && (
          <>
            <DropZone label="PDF Document *" hint="Maximum file size: 10MB" accept="application/pdf" file={pdfFile} onFile={setPdfFile} />
            {pdfFile && (
              <div>
                <label style={labelStyle}>PDF Document Title *</label>
                <input required value={pdfTitle} onChange={(e) => setPdfTitle(e.target.value)} placeholder="Enter PDF document title (e.g., Safety Procedures Manual)" style={inputStyle} />
                <p style={hintStyle}>This title will be displayed to employees when they view the course.</p>
              </div>
            )}
          </>
        )}
        <div>
          <label style={labelStyle}>Passing Score (%)</label>
          <input type="number" min={0} max={100} value={passingScore} onChange={(e) => setPassingScore(Number(e.target.value))} style={{ ...inputStyle, width: 100 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>Duration</label>
            <input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g., 45 min, 1 hr 30 min, 2 hr" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Category</label>
            <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g., Leadership, Marketing, IT" style={inputStyle} />
          </div>
        </div>
        <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Quiz Settings</h3>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={randomizeQuestions} onChange={(e) => setRandomizeQuestions(e.target.checked)} style={{ width: 20, height: 20 }} />
            <div>
              <span style={{ fontWeight: 'bold' }}>Randomize Question Order</span>
              <p style={{ ...hintStyle, margin: '0.25rem 0 0' }}>When enabled, quiz questions will appear in a different random order for each employee and each attempt.</p>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={randomizeOptions} onChange={(e) => setRandomizeOptions(e.target.checked)} style={{ width: 20, height: 20 }} />
            <div>
              <span style={{ fontWeight: 'bold' }}>Randomize Answer Options</span>
              <p style={{ ...hintStyle, margin: '0.25rem 0 0' }}>When enabled, answer options will appear in a different random order for each attempt.</p>
            </div>
          </label>
          <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e0e0' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={useQuestionPool} onChange={(e) => setUseQuestionPool(e.target.checked)} style={{ width: 20, height: 20 }} />
              <div>
                <span style={{ fontWeight: 'bold' }}>Question Pool Mode</span>
                <p style={{ ...hintStyle, margin: '0.25rem 0 0' }}>Create more questions than will appear on the quiz. Each employee gets a random subset.</p>
              </div>
            </label>
            {useQuestionPool && (
              <div style={{ marginLeft: '2rem', marginTop: '1rem', padding: '1rem', background: '#f0f7ff', borderRadius: 8, border: '1px solid #b3d9ff' }}>
                <label style={labelStyle}>Total Questions in Pool *</label>
                <input type="number" min={2} max={50} value={poolSize} onChange={(e) => setPoolSize(Number(e.target.value))} style={{ ...inputStyle, width: 150 }} />
                <label style={{ ...labelStyle, marginTop: '1rem' }}>Questions to Display per Quiz *</label>
                <input type="number" min={1} max={poolSize} value={questionsToDisplay} onChange={(e) => setQuestionsToDisplay(Number(e.target.value))} style={{ ...inputStyle, width: 150 }} />
              </div>
            )}
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Quiz Questions</h3>
            <button type="button" onClick={() => setQuiz((q) => [...q, blankQuestion()])} style={primaryButton(false)}>
              Add Question
            </button>
          </div>
          {quiz.map((question, questionIndex) => (
            <div key={questionIndex} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '1.5rem', marginBottom: '1rem', background: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0 }}>Question {questionIndex + 1}</h4>
                {quiz.length > 1 && (
                  <button type="button" onClick={() => setQuiz((q) => q.filter((_, i) => i !== questionIndex))} style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer' }}>
                    ✕ Remove
                  </button>
                )}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Question Type:</label>
                <select value={question.questionType} onChange={(e) => setType(questionIndex, e.target.value as QType)} style={{ ...inputStyle, background: 'white' }}>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="true_false">True/False</option>
                  <option value="fill_blank">Fill in the Blank</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Question:</label>
                <input
                  value={question.question}
                  onChange={(e) => setQuiz((prev) => prev.map((q, i) => (i === questionIndex ? { ...q, question: e.target.value } : q)))}
                  placeholder="Enter your question"
                  style={inputStyle}
                />
              </div>
              {question.questionType === 'fill_blank' ? (
                <div>
                  <label style={labelStyle}>Correct Answer(s) *</label>
                  <input
                    value={question.correctAnswerText}
                    onChange={(e) => setQuiz((prev) => prev.map((q, i) => (i === questionIndex ? { ...q, correctAnswerText: e.target.value } : q)))}
                    placeholder="Enter accepted answers separated by commas"
                    style={inputStyle}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', marginTop: '0.75rem' }}>
                    <input type="checkbox" checked={question.caseSensitive} onChange={(e) => setQuiz((prev) => prev.map((q, i) => (i === questionIndex ? { ...q, caseSensitive: e.target.checked } : q)))} style={{ marginRight: '0.5rem', width: 18, height: 18 }} />
                    Case-sensitive matching
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}>
                    <input type="checkbox" checked={question.fuzzyMatching} onChange={(e) => setQuiz((prev) => prev.map((q, i) => (i === questionIndex ? { ...q, fuzzyMatching: e.target.checked } : q)))} style={{ marginRight: '0.5rem', width: 18, height: 18 }} />
                    Enable fuzzy matching (allows up to 2 character differences)
                  </label>
                </div>
              ) : (
                <div>
                  {question.options.map((opt, optionIndex) => (
                    <div key={optionIndex} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="radio"
                        name={`correct-${questionIndex}`}
                        checked={question.correctAnswer === optionIndex}
                        onChange={() => setQuiz((prev) => prev.map((q, i) => (i === questionIndex ? { ...q, correctAnswer: optionIndex } : q)))}
                      />
                      <input
                        value={opt}
                        disabled={question.questionType === 'true_false'}
                        onChange={(e) =>
                          setQuiz((prev) =>
                            prev.map((q, i) =>
                              i === questionIndex
                                ? { ...q, options: q.options.map((o, oi) => (oi === optionIndex ? e.target.value : o)) }
                                : q
                            )
                          )
                        }
                        placeholder={`Option ${optionIndex + 1}`}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                    </div>
                  ))}
                  <p style={hintStyle}>Select the radio button next to the correct answer.</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={actionsStyle}>
          <button type="button" onClick={onCancel} style={{ padding: '0.75rem 1.5rem', background: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', fontSize: '1rem' }}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} style={primaryButton(submitting)}>
            {submitting ? 'Creating...' : 'Create Course'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CourseCreateForm;
